import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { CreateUserModel } from "src/app/models/create-user-model";
import { User } from "src/app/models/user-model";
import { RegistrationService } from "src/app/services/registration.service";
import { RegistrationState } from "./register.state";

const initialState: RegistrationState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    createUser: null,
    user: null,
};

@Injectable({
    providedIn: 'root'
})
export class RegisterComponentStore extends ComponentStore<RegistrationState> {

    constructor(private readonly registrationService: RegistrationService) {
        super(initialState);
    }

    readonly createUserModel$: Observable<CreateUserModel> = this.select(state => state.createUser);
    readonly user$: Observable<User> = this.select(state => state.user);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.createUserModel$,
        this.user$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (createUserModel, user, loaded, loading, success, errorMessage) => ({
            createUserModel, user
            , loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater((_: RegistrationState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: RegistrationState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: RegistrationState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly createUserState = this.updater((state: RegistrationState, value: {
        createUserModel: CreateUserModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            createUser: value.createUserModel
        };
    });

    readonly updateCreateUserState = this.updater((state: RegistrationState, value: {
        user: User
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            user: value.user
        };
    });

    readonly setError = this.updater((state: RegistrationState, value: {
        errorMessage: string
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly createUser = this.effect((createUserParams$: Observable<{ createUserModel: CreateUserModel }>) => {
        this.setLoading();
        return createUserParams$.pipe(
            switchMap((params) => this.registrationService.createUser(params.createUserModel).pipe(
                map((user) => {
                    this.updateCreateUserState({ user: user });
                    this.setLoaded();
                    this.setInitial();
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                    this.setInitial();
                })
            ))
        )
    });
}   